import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from './Sidebar';
import Topbar from './Topbar';
import PageHeader from './PageHeader';
import MetricCard from './MetricCard';
import RedemptionsChart from './charts/RedemptionsChart';
import RedemptionsByTypeChart from './charts/RedemptionsByTypeChart';
import RewardDetailDrawer from './RewardDetailDrawer';
import { AISidebar, AIRecommendationPanel } from './ai';
import { AIOutlineButton } from './ui/AIOutlineButton';
import { Award, Users, Coins, Gift, Plus, Play, Pause, Calendar, SkipBack, Gem, Receipt, ShoppingBag } from 'lucide-react';
import { redemptionsOverTime, redemptionsByType, topRewards } from '../utils/mockData';
type AIContext = 'general' | 'redemptions' | 'types' | 'top-rewards';
const Dashboard = () => {
  const navigate = useNavigate();
  const [selectedReward, setSelectedReward] = useState<any>(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isAISidebarOpen, setIsAISidebarOpen] = useState(false);
  const [aiContext, setAiContext] = useState<AIContext>('general');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const handleRewardClick = (reward: any) => {
    setSelectedReward(reward);
    setIsDrawerOpen(true);
  };
  const handleCloseDrawer = () => {
    setIsDrawerOpen(false);
    setTimeout(() => setSelectedReward(null), 300);
  };
  const handleCreateReward = () => {
    navigate('/rewards/new');
  };
  // AI Context Handlers
  const handleGeneralAI = () => {
    setAiContext('general');
    setIsAISidebarOpen(true);
  };
  const handleRedemptionsAI = () => {
    setAiContext('redemptions');
    setIsAISidebarOpen(true);
  };
  const handleTypesAI = () => {
    setAiContext('types');
    setIsAISidebarOpen(true);
  };
  const handleTopRewardsAI = () => {
    setAiContext('top-rewards');
    setIsAISidebarOpen(true);
  };
  // Get AI Sidebar Title based on context
  const getAISidebarTitle = () => {
    switch (aiContext) {
      case 'redemptions':
        return 'Suggerimenti AI: Trend Riscatti';
      case 'types':
        return 'Suggerimenti AI: Distribuzione Premi';
      case 'top-rewards':
        return 'Suggerimenti AI: Best Sellers';
      default:
        return 'Suggerimenti AI per la Dashboard';
    }
  };
  // Get AI Recommendations based on context
  const getAIRecommendations = () => {
    switch (aiContext) {
      case 'redemptions':
        return [{
          id: '1',
          title: 'Capitalizza il trend positivo',
          type: 'Crescita',
          diamondRange: '+12.5% ultimo mese',
          reason: "I riscatti sono in crescita costante del 12.5% nell'ultimo mese. Il picco si verifica nei weekend (+35%). Lancia premi speciali il venerdì per massimizzare engagement e conversioni.",
          ctaLabel: 'Crea premio weekend',
          onCtaClick: () => navigate('/rewards/new')
        }, {
          id: '2',
          title: 'Ottimizza mid-week',
          type: 'Opportunità',
          diamondRange: 'Mercoledì-Giovedì',
          reason: 'Calo del 20% a metà settimana rilevato. Implementa notifiche push o email reminder per stimolare attività nei giorni meno performanti e livellare la distribuzione.',
          ctaLabel: 'Vedi strategia',
          onCtaClick: () => navigate('/insights')
        }];
      case 'types':
        return [{
          id: '1',
          title: 'Potenzia Prodotti Viso',
          type: 'Best Performer',
          diamondRange: '35% dei riscatti',
          reason: "I prodotti viso dominano con il 35% dei riscatti totali, seguiti dai cosmetici solidi (28%). Aumenta l'offerta in queste categorie del 30% per capitalizzare la forte domanda.",
          ctaLabel: 'Crea prodotto viso',
          onCtaClick: () => navigate('/rewards/new')
        }, {
          id: '2',
          title: 'Rivedi strategia Esperienze',
          type: 'Attenzione',
          diamondRange: '-15% vs periodo precedente',
          reason: 'Le esperienze sono in calo del 15%. Gli utenti preferiscono prodotti fisici tangibili. Considera di ridurre le esperienze del 20% e reinvestire budget in prodotti viso e cosmetici solidi.',
          ctaLabel: 'Analizza trend',
          onCtaClick: () => navigate('/insights')
        }];
      case 'top-rewards':
        return [{
          id: '1',
          title: 'Aumenta stock best-sellers',
          type: 'Urgente',
          diamondRange: 'Acido Ialuronico + Kit Solidi',
          reason: 'I tuoi top 2 premi (Acido Ialuronico: 234 riscatti, Kit Cosmetici Solidi: 187 riscatti) hanno disponibilità in calo. Aumenta lo stock del 40% per evitare esaurimento e perdita di momentum.',
          ctaLabel: 'Aggiorna disponibilità',
          onCtaClick: () => navigate('/rewards/1')
        }, {
          id: '2',
          title: 'Crea varianti vincenti',
          type: 'Strategia',
          diamondRange: '50-60 punti range',
          reason: 'Acido Ialuronico e Kit Cosmetici Solidi performano eccezionalmente nella fascia 50-60 punti. Crea varianti simili (es. altri sieri attivi, kit tematici diversi) nella stessa fascia di prezzo per replicare il successo.',
          ctaLabel: 'Duplica e modifica',
          onCtaClick: () => navigate('/rewards/new')
        }];
      default:
        return [{
          id: '1',
          title: 'Ottimizza fascia 40-60 punti',
          type: 'Opportunità',
          diamondRange: '40-60 punti',
          reason: "I riscatti sono aumentati del 12.5%. Questa fascia ha il miglior tasso di conversione. Crea nuovi premi in questo range per massimizzare l'engagement.",
          ctaLabel: 'Crea premio',
          onCtaClick: () => navigate('/rewards/new')
        }, {
          id: '2',
          title: 'Capitalizza crescita utenti',
          type: 'Strategia',
          diamondRange: '+8.2% utenti attivi',
          reason: "Gli utenti unici sono cresciuti dell'8.2%. Lancia premi esclusivi per nuovi utenti o programmi fedeltà per consolidare la retention.",
          ctaLabel: 'Vedi insights',
          onCtaClick: () => navigate('/insights')
        }];
    }
  };
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'Attivo':
        return <Play className="h-4 w-4 text-green-600" data-id="element-424" />;
      case 'Programmato':
        return <Calendar className="h-4 w-4 text-blue-600" data-id="element-425" />;
      case 'In Scadenza':
        return <Pause className="h-4 w-4 text-amber-600" data-id="element-426" />;
      case 'Esaurito':
        return <SkipBack className="h-4 w-4 text-gray-600" data-id="element-427" />;
      default:
        return null;
    }
  };
  return <div className="flex h-screen bg-gray-50" data-id="element-428">
      {/* Desktop Sidebar Wrapper - Hidden on Mobile */}
      <div className="hidden lg:block w-20 flex-shrink-0" data-id="element-429">
        <Sidebar data-id="element-430" />
      </div>

      {/* Mobile Sidebar - Overlay */}
      <div className="lg:hidden" data-id="element-431">
        <Sidebar mobileOpen={sidebarOpen} onMobileClose={() => setSidebarOpen(false)} data-id="element-432" />
      </div>

      <div className="flex-1 flex flex-col overflow-hidden" data-id="element-433">
        <Topbar onMenuClick={() => setSidebarOpen(true)} data-id="element-434" />
        <main className="flex-1 overflow-y-auto p-4 md:p-6" data-id="element-435">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-6 gap-4" data-id="element-436">
            <div data-id="element-437">
              <h1 className="text-2xl font-semibold text-gray-900" data-id="element-438">
                Panoramica
              </h1>
              <p className="text-sm text-gray-500 mt-1" data-id="element-439">
                Andamento sintetico e azioni rapide.
              </p>
            </div>
            <div className="flex items-center gap-3" data-id="element-440">
              <AIOutlineButton onClick={handleGeneralAI} title="AI Insights" data-id="element-441">
                Suggerimenti AI
              </AIOutlineButton>
              <button onClick={handleCreateReward} className="inline-flex items-center px-4 py-2 bg-greenback text-white text-sm font-medium hover:bg-greenback-600 transition-colors" style={{
              borderRadius: '100px'
            }} data-id="element-442">
                <Plus className="h-4 w-4 mr-2" data-id="element-443" />
                Crea Premio
              </button>
            </div>
          </div>

          {/* KPI Cards - Aligned with widgets below */}
          <div className="mb-8 grid grid-cols-1 gap-6 lg:grid-cols-2" data-id="element-444">
            {/* Left column - 2 KPIs aligned with left widget */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6" data-id="element-445">
              <MetricCard title="Premi Attivi" value="28" change="3" isPositive={true} icon={Gift} data-id="element-446" />
              <MetricCard title="Riscatti Totali" value="817" change="12.5%" isPositive={true} icon={Receipt} data-id="element-447" />
            </div>

            {/* Right column - 2 KPIs aligned with right widget */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6" data-id="element-448">
              <MetricCard title="Utilizzi Totali" value="572" change="10.8%" isPositive={true} icon={ShoppingBag} data-id="element-449" />
              <MetricCard title="Crediti Diamante Ricevuti" value="49,660" change="15.3%" isPositive={true} icon={Gem} data-id="element-450" />
            </div>
          </div>

          <div className="mb-8 grid grid-cols-1 gap-6 lg:grid-cols-2" data-id="element-451">
            <RedemptionsChart data={redemptionsOverTime} onAIClick={handleRedemptionsAI} data-id="element-452" />
            <RedemptionsByTypeChart data={redemptionsByType} onAIClick={handleTypesAI} data-id="element-453" />
          </div>

          <div className="rounded-lg border border-gray-200 bg-white" data-id="element-454">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between" data-id="element-455">
              <h3 className="text-lg font-semibold" data-id="element-456">Top Premi</h3>
              <AIOutlineButton onClick={handleTopRewardsAI} title="AI Insights" data-id="element-457">
                Suggerimenti AI
              </AIOutlineButton>
            </div>
            <div className="overflow-x-auto" data-id="element-458">
              <table className="w-full" data-id="element-459">
                <thead data-id="element-460">
                  <tr className="bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-461">
                    <th className="px-6 py-3" data-id="element-462">Premio</th>
                    <th className="px-6 py-3" data-id="element-463">Stato</th>
                    <th className="px-6 py-3" data-id="element-464">Riscatti (Periodo)</th>
                    <th className="px-6 py-3" data-id="element-465">Punti Spesi</th>
                    <th className="px-6 py-3" data-id="element-466">Ultimo Riscatto</th>
                    <th className="px-6 py-3" data-id="element-467">Azioni</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200" data-id="element-468">
                  {topRewards.map(reward => <tr key={reward.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => handleRewardClick(reward)} data-id="element-469">
                      <td className="px-6 py-4" data-id="element-470">
                        <div className="font-medium text-gray-900" data-id="element-471">
                          {reward.name}
                        </div>
                        <div className="text-sm text-gray-500" data-id="element-472">
                          {reward.category}
                        </div>
                      </td>
                      <td className="px-6 py-4" data-id="element-473">
                        <div className="inline-flex items-center justify-center w-8 h-8 rounded-full hover:bg-gray-100 cursor-help" title={reward.status} data-id="element-474">
                          {getStatusIcon(reward.status)}
                        </div>
                      </td>
                      <td className="px-6 py-4 font-medium" data-id="element-475">
                        {reward.redemptions}
                      </td>
                      <td className="px-6 py-4 font-medium" data-id="element-476">
                        <div className="flex items-center gap-1" data-id="element-477">
                          <Gem className="h-3.5 w-3.5 text-gray-500" data-id="element-478" />
                          <span data-id="element-479">{reward.diamondsSpent.toLocaleString()}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-gray-500" data-id="element-480">
                        {reward.lastRedemption}
                      </td>
                      <td className="px-6 py-4" data-id="element-481">
                        <button onClick={e => {
                      e.stopPropagation();
                      handleRewardClick(reward);
                    }} className="text-greenback hover:text-greenback-600 font-medium text-sm" data-id="element-482">
                          Dettagli
                        </button>
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>
          </div>
        </main>
      </div>

      {/* AI Sidebar with contextual content */}
      <AISidebar isOpen={isAISidebarOpen} onClose={() => {
      setIsAISidebarOpen(false);
      setAiContext('general');
    }} title={getAISidebarTitle()} data-id="element-483">
        <AIRecommendationPanel recommendations={getAIRecommendations()} lastUpdated="Ultimi 30 giorni" data-id="element-484" />
      </AISidebar>

      <RewardDetailDrawer isOpen={isDrawerOpen} onClose={handleCloseDrawer} reward={selectedReward} data-id="element-485" />
    </div>;
};
export default Dashboard;